import {
  getNotificationsCentreCategoryLabel,
  isNotificationsCentreCategory,
} from "@/constants/notificationsCentreCategories"
import type { NotificationCategory } from "@/types/notifications"

export const NOTIFICATIONS_CENTRE_BASE_PATH = "/notifications"

export const NOTIFICATIONS_CENTRE_RETURN_PATH_KEY =
  "notifications_centre_sidebar_return_path"

export const NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL = "Notifications"

export const NOTIFICATIONS_CENTRE_CATEGORY_SEGMENT = "category"

/** sidebar-09 inbox list width — room for preview text + multi-select checkboxes. */
export const NOTIFICATIONS_CENTRE_SIDEBAR_WIDTH = "440px"

/** Matches `ShadcnSidebarInsetHeader` expanded row (py-3 + h-12). */
export const NOTIFICATIONS_CENTRE_INBOX_HEADER_CLASS =
  "flex shrink-0 border-b border-border px-4 py-3"

export const NOTIFICATIONS_CENTRE_INBOX_HEADER_INNER_CLASS =
  "flex h-12 w-full items-center"

/** Matches inset content wrapper top padding (`DashboardShell` p-4). */
export const NOTIFICATIONS_CENTRE_INBOX_LIST_CLASS =
  "flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto px-4 pt-4"

/** Notifications loaded per API request (initial + each scroll page). */
export const NOTIFICATIONS_INBOX_PAGE_SIZE = 50

export function notificationsCentrePath(): string {
  return NOTIFICATIONS_CENTRE_BASE_PATH
}

export function notificationsCentreCategoryPath(category: NotificationCategory): string {
  return `${NOTIFICATIONS_CENTRE_BASE_PATH}/${NOTIFICATIONS_CENTRE_CATEGORY_SEGMENT}/${encodeURIComponent(category)}`
}

export function notificationsCentreNotificationPath(notificationId: string): string {
  return `${NOTIFICATIONS_CENTRE_BASE_PATH}/${encodeURIComponent(notificationId)}`
}

export function notificationsCentreCategoryNotificationPath(
  category: NotificationCategory,
  notificationId: string
): string {
  return `${notificationsCentreCategoryPath(category)}/${encodeURIComponent(notificationId)}`
}

export function resolveNotificationsCentreCategory(
  pathname: string
): NotificationCategory | null {
  const segments = pathname
    .replace(new RegExp(`^${NOTIFICATIONS_CENTRE_BASE_PATH}/?`), "")
    .split("/")
    .filter(Boolean)

  if (segments[0] !== NOTIFICATIONS_CENTRE_CATEGORY_SEGMENT) {
    return null
  }

  const raw = segments[1] ? decodeURIComponent(segments[1]) : null
  return isNotificationsCentreCategory(raw) ? raw : null
}

export function resolveNotificationsCentreNotificationId(
  pathname: string
): string | null {
  const segments = pathname
    .replace(new RegExp(`^${NOTIFICATIONS_CENTRE_BASE_PATH}/?`), "")
    .split("/")
    .filter(Boolean)

  if (segments.length === 0) {
    return null
  }

  if (segments[0] === NOTIFICATIONS_CENTRE_CATEGORY_SEGMENT) {
    const raw = segments[2] ? decodeURIComponent(segments[2]) : null
    return raw || null
  }

  const raw = decodeURIComponent(segments[0] ?? "")
  if (!raw || raw === NOTIFICATIONS_CENTRE_CATEGORY_SEGMENT) {
    return null
  }
  return raw
}

export function notificationsCentreListLabel(
  category: NotificationCategory | null
): string {
  if (!category) return "Inbox"
  return getNotificationsCentreCategoryLabel(category)
}

export function isNotificationsCentrePathname(pathname: string): boolean {
  return (
    pathname === NOTIFICATIONS_CENTRE_BASE_PATH ||
    pathname.startsWith(`${NOTIFICATIONS_CENTRE_BASE_PATH}/`)
  )
}

/** Sidebar account menu — unread suffix for the Notifications row. */
export function formatNotificationsProfileMenuLabel(unreadCount: number): string {
  if (unreadCount <= 0) {
    return "Notifications"
  }
  if (unreadCount > 9) {
    return "Notifications (9+ unread)"
  }
  return `Notifications (${unreadCount} unread)`
}

export {
  getNotificationsCentreCategoryLabel,
  isNotificationsCentreCategory,
  NOTIFICATIONS_CENTRE_CATEGORY_LINKS,
  NOTIFICATIONS_CENTRE_HIDDEN_SIDEBAR_CATEGORIES,
  NOTIFICATIONS_CENTRE_INBOX_LINK,
  NOTIFICATIONS_CENTRE_SIDEBAR_CATEGORY_LINKS,
  type NotificationsCentreCategoryLink,
} from "@/constants/notificationsCentreCategories"
